import type { AppRole } from "@/lib/auth-context";

export type InviteBrand = {
  tenantName: string;
  /** Optional tenant logo URL — included as a visible link line in the email body. */
  logoUrl?: string | null;
  primaryColor?: string;
  accentColor?: string;
  supportEmail?: string;
  /** Footer/signature line shown at the bottom of the email (falls back to "Powered by TriSphere"). */
  footerText?: string;
};

type InviteTemplateInput = InviteBrand & {
  role: AppRole;
  email: string;
  inviteUrl: string;
  expiresAt: string;
  /** Deprecated alias for `footerText` — kept so old call sites still compile. */
  poweredBy?: string;
};

export function roleLabel(role: AppRole | string): string {
  return String(role).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function resolveFooter(input: InviteTemplateInput) {
  return (
    input.footerText?.trim() ||
    input.poweredBy?.trim() ||
    "Powered by TriSphere"
  );
}

/**
 * Plain-text email body. `mailto:` links can only carry text bodies, but we
 * include the tenant name, role, logo URL, support address, and colour signature
 * so the message is unmistakably tenant-branded even in a plain-text view.
 */
export function buildTenantInviteEmail(input: InviteTemplateInput) {
  const expiry = new Date(input.expiresAt).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  const subject = `${input.tenantName} · ${roleLabel(input.role)} portal invitation`;
  const brandRule = `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;
  const support = input.supportEmail
    ? `\nQuestions? Reply to ${input.supportEmail}.`
    : "";
  const logoLine = input.logoUrl
    ? `\n${input.tenantName} logo: ${input.logoUrl}\n`
    : "";
  const paletteLine =
    input.primaryColor && input.accentColor
      ? `\nBrand palette · primary ${input.primaryColor} · accent ${input.accentColor}`
      : "";

  const body = [
    brandRule,
    `  ${input.tenantName.toUpperCase()}`,
    brandRule,
    ``,
    `Hello,`,
    ``,
    `You've been invited to join ${input.tenantName} as ${roleLabel(input.role)}.`,
    logoLine.trim(),
    ``,
    `Accept your secure tenant-locked invitation:`,
    input.inviteUrl,
    ``,
    `This link expires on ${expiry} and can only be used once by ${input.email}.`,
    support,
    paletteLine,
    ``,
    brandRule,
    resolveFooter(input),
    brandRule,
  ]
    .filter((l) => l !== "")
    .join("\n");

  return { subject, body };
}

export function buildTenantInviteSms(input: InviteTemplateInput) {
  const expiry = new Date(input.expiresAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  return `${input.tenantName}: You have a ${roleLabel(input.role)} portal invite. Expires ${expiry}. Accept: ${input.inviteUrl}`;
}

export function openEmailInvite(input: InviteTemplateInput) {
  const { subject, body } = buildTenantInviteEmail(input);
  const href = `mailto:${encodeURIComponent(input.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = href;
}

export function openSmsInvite(input: InviteTemplateInput, phone?: string) {
  const body = buildTenantInviteSms(input);
  const recipient = phone?.trim() ? encodeURIComponent(phone.trim()) : "";
  window.location.href = `sms:${recipient}?body=${encodeURIComponent(body)}`;
}
